import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styles from './content-slide.module.scss';
import { ContentDataPDFType } from '../../types';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';
import Button from '../button/Button';
import Spinner from '../spinner/Spinner';
import { useNextContent } from '../../pages/program-details/useNextContent';

type Props = {
  data: ContentDataPDFType;
};

function ContentSlide(props: Props) {
  const { data } = props;


  const { hasNextContent, selectNext } = useNextContent();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number | undefined>(undefined);
  const [height, setHeight] = useState<number | undefined>(undefined);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    setPageNumber(1);
    setTotalPages(0);
  }, [data]);

  const fileObj = useMemo(() => {
    return data;
  }, [data]);

  const onDocumentLoadSuccess = useCallback(({ numPages }) => {
    setTotalPages(numPages);
  }, []);

  const calculateSize = useCallback(() => {
    if (wrapperRef.current) {
      const { width, height } = wrapperRef.current.getBoundingClientRect();
      setWidth(width);
      setHeight(height);
    }
  }, []);

  useLayoutEffect(() => {
    window.addEventListener('resize', calculateSize);

    calculateSize();
    return () => {
      window.removeEventListener('resize', calculateSize);
    };
  }, [calculateSize]);

  useEffect(() => {
    setTimeout(calculateSize, 50);
  }, [calculateSize]);

  const prevDisabled = pageNumber <= 1;
  const isLast = pageNumber >= totalPages;

  const continueButton = useMemo(() => {
    if (isLast && !hasNextContent) return null;
    return (
      <Button onClick={selectNext} label={'Continue'}/>
    );
  }, [selectNext, isLast, hasNextContent]);

  if (!fileObj) return null;

  const handleLeft = () => {
    setPageNumber(Math.max(pageNumber - 1, 1));
  };

  const handleRight = () => {
    setPageNumber(Math.min(pageNumber + 1, totalPages));
  };

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <div className={styles.content}>
        <Document
          file={fileObj}
          onLoadSuccess={onDocumentLoadSuccess}
          className={styles.document}
          loading={<Spinner/>}
        >
          <Page loading={<Spinner/>} pageNumber={pageNumber} width={width}
                height={height && totalPages > 1 ? height - 80 : height}/>
        </Document>
      </div>

      <div className={styles.actions}>
        {totalPages > 1 &&
        <>
          <Button disabled={prevDisabled} onClick={handleLeft} label={'Previous'}/>
          <div className={styles.pages}>
            <span className={styles.currentPage}>{pageNumber}</span>/{totalPages}
          </div>
          {isLast ? continueButton : <Button disabled={isLast} onClick={handleRight} label={'Next'}/>}
        </>
        }
        {totalPages <= 1 && continueButton && <><div/><div/>{continueButton}</>}
      </div>
    </div>
  );
}

export default ContentSlide;
